import React, { useEffect, useState, useCallback } from 'react';
import MerchantContextPanel from '../components/MerchantContextPanel';
import { ProposalBuilder } from '../components/proposals/ProposalBuilder';
import { Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Upload, FileText, BarChart3, DollarSign, Zap, TrendingUp,
  CheckCircle2, ArrowRight, Loader2, Eye, Send, Copy,
  Package, Calculator, ExternalLink, FileCheck, Mail, Clock, Download
} from 'lucide-react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

async function openStatementFile(statementId, mode) {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/statements/${statementId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to load file');
    const disposition = res.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : `${statementId}.pdf`;
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    if (mode === 'view') {
      window.open(url, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  } catch (err) {
    toast.error('Could not load statement file');
  }
}

export default function StatementAnalysisPage() {
  const { user } = useAuthStore();
  const isAgent = ['iso_agent', 'iso_subagent'].includes(user?.role);
  const [tab, setTab] = useState('analyze');
  const [statements, setStatements] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, p] = await Promise.all([
        api.request('/statements'),
        api.request('/proposals'),
      ]);
      setStatements(s);
      setProposals(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="statement-analysis-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Statement Analysis & Proposals</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload statements, calculate pricing, generate proposals</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="analyze" data-testid="tab-analyze"><Calculator className="h-4 w-4 mr-1.5" /> Analyze</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements"><FileText className="h-4 w-4 mr-1.5" /> Statements ({statements.length})</TabsTrigger>
          <TabsTrigger value="proposals" data-testid="tab-proposals"><FileCheck className="h-4 w-4 mr-1.5" /> Proposals ({proposals.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="analyze">
          <AnalyzeTab onRefresh={load} isAgent={isAgent} />
        </TabsContent>

        <TabsContent value="statements">
          <StatementsTab statements={statements} loading={loading} />
        </TabsContent>

        <TabsContent value="proposals">
          <ProposalsTab proposals={proposals} loading={loading} onRefresh={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Analyze Tab — Upload + Pricing Engine + Proposal ──────────

function AnalyzeTab({ onRefresh, isAgent }) {
  const { user } = useAuthStore();
  const [step, setStep] = useState('input'); // input, pricing, proposal
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [statementId, setStatementId] = useState(null);

  // Manual input fields
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [monthlyVolume, setMonthlyVolume] = useState('');
  const [currentRate, setCurrentRate] = useState('');
  const [currentFees, setCurrentFees] = useState('');
  const [leadId, setLeadId] = useState('');

  // Pricing state
  const [pricingResult, setPricingResult] = useState(null);
  const [slider, setSlider] = useState([50]);
  const [selectedModel, setSelectedModel] = useState('dual_pricing');
  const [calculatingPricing, setCalculatingPricing] = useState(false);

  // Proposal state
  const [proposal, setProposal] = useState(null);
  const [industry, setIndustry] = useState('restaurant');
  const [contactEmail, setContactEmail] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailScheduleAt, setEmailScheduleAt] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailSending, setEmailSending] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    if (leadId) formData.append('lead_id', leadId);

    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE}/api/statements/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Upload failed');

      setStatementId(data.statement_id);
      setUploading(false);
      setAnalyzing(true);

      // Auto-analyze
      const analysisResp = await api.request(`/statements/${data.statement_id}/analyze`, { method: 'POST' });
      setAnalysis(analysisResp.analysis);

      // Pre-fill from analysis
      if (analysisResp.analysis) {
        const a = analysisResp.analysis;
        if (a.total_volume) setMonthlyVolume(String(a.total_volume));
        if (a.effective_rate) setCurrentRate(String((a.effective_rate * 100).toFixed(2)));
        if (a.total_fees) setCurrentFees(String(a.total_fees));
        if (a.processor_name && !businessName) setBusinessName(`${a.processor_name} Merchant`);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const calculatePricing = async () => {
    const vol = parseFloat(monthlyVolume);
    if (!vol || vol <= 0) { alert('Enter a valid monthly volume'); return; }
    setCalculatingPricing(true);

    try {
      const result = await api.request('/pricing/calculate', {
        method: 'POST',
        body: JSON.stringify({
          monthly_volume: vol,
          current_effective_rate: currentRate ? parseFloat(currentRate) / 100 : undefined,
          current_total_fees: currentFees ? parseFloat(currentFees) : undefined,
          profitability_slider: slider[0] / 100,
          statement_id: statementId,
        }),
      });
      setPricingResult(result);
      setStep('pricing');
    } catch (e) {
      alert(e.message || 'Pricing calculation failed');
    } finally {
      setCalculatingPricing(false);
    }
  };

  const generateProposal = async () => {
    try {
      const result = await api.request('/proposals', {
        method: 'POST',
        body: JSON.stringify({
          statement_id: statementId,
          lead_id: leadId || null,
          business_name: businessName || 'Merchant',
          contact_name: contactName,
          contact_email: contactEmail,
          industry: industry,
          monthly_volume: parseFloat(monthlyVolume),
          current_fees: currentFees ? parseFloat(currentFees) : null,
          current_effective_rate: currentRate ? parseFloat(currentRate) / 100 : null,
          selected_pricing: selectedModel,
          profitability_slider: slider[0] / 100,
        }),
      });
      setProposal(result);
      setStep('proposal');
      onRefresh();
    } catch (e) {
      alert(e.message || 'Failed to generate proposal');
    }
  };

  const handlePreviewEmail = async () => {
    try {
      const result = await api.request('/email/preview', {
        method: 'POST',
        body: JSON.stringify({
          proposal_id: proposal.proposal_id,
          to_email: emailTo || contactEmail,
          to_name: contactName,
          industry: industry,
        }),
      });
      setEmailPreview(result);
    } catch (e) {
      alert('Preview failed: ' + e.message);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo && !contactEmail) return alert('Please enter a recipient email');
    setEmailSending(true);
    try {
      const result = await api.request('/email/send-proposal', {
        method: 'POST',
        body: JSON.stringify({
          proposal_id: proposal.proposal_id,
          to_email: emailTo || contactEmail,
          to_name: contactName,
          industry: industry,
          schedule_at: emailScheduleAt || null,
        }),
      });
      setShowEmailDialog(false);
      setEmailPreview(null);
      alert(result.message || 'Email sent!');
    } catch (e) {
      alert('Send failed: ' + e.message);
    } finally {
      setEmailSending(false);
    }
  };

  // Re-calculate when slider changes
  useEffect(() => {
    if (step === 'pricing' && monthlyVolume) {
      const timer = setTimeout(() => {
        api.request('/pricing/calculate', {
          method: 'POST',
          body: JSON.stringify({
            monthly_volume: parseFloat(monthlyVolume),
            current_effective_rate: currentRate ? parseFloat(currentRate) / 100 : undefined,
            current_total_fees: currentFees ? parseFloat(currentFees) : undefined,
            profitability_slider: slider[0] / 100,
          }),
        }).then(setPricingResult).catch(() => {});
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [slider]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 1: Input ──
  if (step === 'input') {
    if (user?.is_sandbox) {
      return (
        <Card className="p-8 text-center" data-testid="analyze-sandbox-disabled">
          <Calculator className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <h3 className="text-lg font-semibold mb-2">Analysis is disabled in this sandbox</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Uploading and analyzing your own statements is turned off in the demo. Check out the Statements
            and Proposals tabs above to see sample results, or sign up to try it with a real statement.
          </p>
        </Card>
      );
    }
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Upload className="h-5 w-5 text-primary" /> Upload Statement
          </h3>
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm">Uploading...</p>
              </div>
            ) : analyzing ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm">Analyzing statement with AI...</p>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground mb-3">Upload a merchant processing statement (PDF)</p>
                <label className="inline-block cursor-pointer">
                  <input type="file" accept=".pdf" onChange={handleUpload} className="hidden" data-testid="statement-file-input" />
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 cursor-pointer">
                    <Upload className="h-4 w-4" /> Choose PDF
                  </span>
                </label>
              </>
            )}
          </div>

          {analysis && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="analysis-result">
              <h4 className="text-sm font-semibold text-green-800 flex items-center gap-1.5 mb-2">
                <CheckCircle2 className="h-4 w-4" /> Statement Analyzed
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {analysis.processor_name && <p><span className="text-muted-foreground">Processor:</span> <strong>{analysis.processor_name}</strong></p>}
                {analysis.total_volume && <p><span className="text-muted-foreground">Volume:</span> <strong>${Number(analysis.total_volume).toLocaleString()}</strong></p>}
                {analysis.total_fees && <p><span className="text-muted-foreground">Total Fees:</span> <strong>${Number(analysis.total_fees).toLocaleString()}</strong></p>}
                {analysis.effective_rate && <p><span className="text-muted-foreground">Effective Rate:</span> <strong>{(analysis.effective_rate * 100).toFixed(2)}%</strong></p>}
                {analysis.pricing_program && analysis.pricing_program !== 'unknown' && (
                  <p><span className="text-muted-foreground">Pricing Program:</span> <strong className="text-amber-700">{
                    { interchange_plus: 'Interchange Plus', flat_rate: 'Flat Rate', err: 'ERR', dual_pricing: 'Dual Pricing', tiered_3: '3-Tier', tiered_2: '2-Tier', tiered_4: '4-Tier' }[analysis.pricing_program] || analysis.pricing_program
                  }</strong></p>
                )}
                {analysis.pricing_program_confidence && (
                  <p><span className="text-muted-foreground">Confidence:</span> <strong>{analysis.pricing_program_confidence}</strong></p>
                )}
              </div>
              {analysis.pricing_program_evidence?.length > 0 && (
                <div className="mt-2 text-xs text-green-700">
                  <span className="font-medium">Evidence:</span> {analysis.pricing_program_evidence.join(', ')}
                </div>
              )}
              {analysis.tiered_pricing_details && (analysis.pricing_program || '').startsWith('tiered') && (
                <div className="mt-2 p-2 bg-amber-50 rounded text-xs">
                  <p className="font-medium text-amber-800 mb-1">Tier Details:</p>
                  <div className="grid grid-cols-2 gap-1 text-amber-700">
                    {analysis.tiered_pricing_details.qual_rate != null && <span>Qual: {(analysis.tiered_pricing_details.qual_rate * 100).toFixed(2)}%</span>}
                    {analysis.tiered_pricing_details.mid_qual_rate != null && <span>Mid-Qual: {(analysis.tiered_pricing_details.mid_qual_rate * 100).toFixed(2)}%</span>}
                    {analysis.tiered_pricing_details.non_qual_rate != null && <span>Non-Qual: {(analysis.tiered_pricing_details.non_qual_rate * 100).toFixed(2)}%</span>}
                    {analysis.tiered_pricing_details.offline_debit_qual_rate != null && <span>Debit Qual: {(analysis.tiered_pricing_details.offline_debit_qual_rate * 100).toFixed(2)}%</span>}
                  </div>
                </div>
              )}
              {analysis.junk_fees?.length > 0 && (
                <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                  <p className="font-medium text-red-700 mb-1">Junk Fees Detected:</p>
                  {analysis.junk_fees.map((f, i) => (
                    <div key={i} className="flex justify-between text-red-600">
                      <span>{f.name} — {f.description}</span>
                      <span className="font-mono">${Number(f.amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              {analysis.fee_breakdown?.length > 0 && (
                <div className="mt-3 border-t pt-2">
                  <p className="text-xs font-medium text-green-700 mb-1">Fee Breakdown:</p>
                  {analysis.fee_breakdown.slice(0, 8).map((f, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span>{f.fee_name}</span>
                      <span className="font-mono">${Number(f.amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Manual Input */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Calculator className="h-5 w-5 text-primary" /> Merchant Info
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Business Name *</label>
              <Input value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="ABC Restaurant" data-testid="business-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Name</label>
              <Input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="John Doe" />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Email</label>
              <Input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="merchant@business.com" data-testid="contact-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Industry</label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger data-testid="industry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="salon_spa">Salon & Spa</SelectItem>
                  <SelectItem value="auto_repair">Auto Repair</SelectItem>
                  <SelectItem value="medical_dental">Medical / Dental</SelectItem>
                  <SelectItem value="ecommerce">E-Commerce</SelectItem>
                  <SelectItem value="specialty_high_risk">Specialty / High-Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Monthly Volume ($) *</label>
                <Input type="number" value={monthlyVolume} onChange={e => setMonthlyVolume(e.target.value)} placeholder="30000" data-testid="volume-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Current Effective Rate (%)</label>
                <Input type="number" step="0.01" value={currentRate} onChange={e => setCurrentRate(e.target.value)} placeholder="3.50" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Current Monthly Fees ($)</label>
                <Input type="number" value={currentFees} onChange={e => setCurrentFees(e.target.value)} placeholder="1050" />
              </div>
              <div>
                <label className="text-sm font-medium">Lead ID (optional)</label>
                <Input value={leadId} onChange={e => setLeadId(e.target.value)} placeholder="Link to CRM lead" />
              </div>
            </div>

            {/* Profitability Slider - Hidden from agents */}
            {!isAgent && (
            <div className="pt-2">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium">Profitability Level</label>
                <span className="text-sm font-mono text-primary">{slider[0]}%</span>
              </div>
              <Slider value={slider} onValueChange={setSlider} min={0} max={100} step={5} data-testid="profitability-slider" />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>Competitive</span>
                <span>Balanced</span>
                <span>Max Profit</span>
              </div>
            </div>
            )}

            <Button onClick={calculatePricing} disabled={!monthlyVolume || calculatingPricing} className="w-full mt-2" data-testid="calculate-pricing-btn">
              {calculatingPricing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</> : <><Zap className="h-4 w-4 mr-2" /> Calculate Pricing</>}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Step 2: Pricing Results ──
  if (step === 'pricing' && pricingResult) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStep('input')} data-testid="back-to-input">&larr; Back to Input</Button>
          {!isAgent && (
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Profitability:</label>
            <div className="w-48">
              <Slider value={slider} onValueChange={setSlider} min={0} max={100} step={5} data-testid="pricing-slider" />
            </div>
            <span className="text-sm font-mono w-10">{slider[0]}%</span>
          </div>
          )}
        </div>

        {/* Current vs Proposed */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-5 border-red-200 bg-red-50/30">
            <h3 className="text-sm font-semibold text-red-700 mb-3">Current Processing</h3>
            <p className="text-3xl font-bold text-red-700">${pricingResult.current_monthly_fees.toLocaleString()}<span className="text-sm font-normal">/mo</span></p>
            <p className="text-sm text-red-600 mt-1">Effective rate: {(pricingResult.current_effective_rate * 100).toFixed(2)}%</p>
          </Card>
          <Card className="p-5 border-green-200 bg-green-50/30">
            <h3 className="text-sm font-semibold text-green-700 mb-3">Best Available Savings</h3>
            <p className="text-3xl font-bold text-green-700">${Math.max(...pricingResult.pricing_options.map(o => o.merchant_savings)).toLocaleString()}<span className="text-sm font-normal">/mo</span></p>
            <p className="text-sm text-green-600 mt-1">Up to {Math.max(...pricingResult.pricing_options.map(o => o.savings_percentage))}% savings</p>
          </Card>
        </div>

        {/* Pricing Options */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {pricingResult.pricing_options.map(option => (
            <Card
              key={option.model}
              className={`p-5 cursor-pointer transition-all ${selectedModel === option.model ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/30'}`}
              onClick={() => setSelectedModel(option.model)}
              data-testid={`pricing-option-${option.model}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm">{option.name}</h4>
                {selectedModel === option.model && <CheckCircle2 className="h-5 w-5 text-primary" />}
              </div>
              <p className="text-xs text-muted-foreground mb-4">{option.description}</p>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Merchant Pays</span>
                  <span className="font-bold">${option.merchant_monthly_cost.toLocaleString()}/mo</span>
                </div>
                <div className="flex justify-between text-sm text-green-700">
                  <span>Merchant Saves</span>
                  <span className="font-bold">${option.merchant_savings.toLocaleString()} ({option.savings_percentage}%)</span>
                </div>
                {!isAgent && (
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span>Your Profit (80%)</span>
                    <span className="font-bold text-primary">${option.iso_share_80.toLocaleString()}/mo</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Agent Share (40%)</span>
                    <span>${option.agent_share_40.toLocaleString()}/mo</span>
                  </div>
                </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Hardware Options */}
        {pricingResult.hardware_options.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" /> Recommended Equipment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pricingResult.hardware_options.map((hw, idx) => (
                <Card key={hw.key} className="p-5 flex gap-4" data-testid={`hardware-option-${idx}`}>
                  <div className="w-24 h-24 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img src={hw.image} alt={hw.name} className="w-full h-full object-contain" onError={e => e.target.style.display='none'} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">{hw.name}</h4>
                      {idx === 0 && <Badge>Recommended</Badge>}
                    </div>
                    <p className="text-lg font-bold text-primary mt-1">${hw.cost.toLocaleString()}</p>
                    <p className="text-xs text-green-600 mt-0.5">ROI: {hw.months_to_roi} months</p>
                    <ul className="mt-2 space-y-0.5">
                      {hw.features.slice(0, 3).map((f, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                          <CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setStep('input')}>Back</Button>
          <Button onClick={generateProposal} data-testid="generate-proposal-btn">
            <FileCheck className="h-4 w-4 mr-2" /> Generate Proposal
          </Button>
        </div>
      </div>
    );
  }

  // ── Step 3: Proposal Generated ──
  if (step === 'proposal' && proposal) {
    const proposalUrl = `${API_BASE}/proposals/view/${proposal.proposal_token}`;
    return (
      <div className="space-y-6">
        <Card className="p-8 text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto text-green-600 mb-4" />
          <h2 className="text-xl font-bold mb-2">Proposal Generated!</h2>
          <p className="text-muted-foreground mb-2">Share this link with the merchant or send it via email.</p>
          <p className="text-xs text-muted-foreground mb-6 flex items-center justify-center gap-1">
            <Clock className="h-3 w-3" />
            Expires in {proposal.expiration_hours || 72} hours
          </p>

          <div className="flex items-center gap-2 max-w-lg mx-auto mb-4">
            <Input value={proposalUrl} readOnly className="font-mono text-sm" data-testid="proposal-url" />
            <Button variant="outline" onClick={() => navigator.clipboard.writeText(proposalUrl)}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => window.open(proposalUrl, '_blank')}>
              <Eye className="h-4 w-4 mr-2" /> Preview
            </Button>
            <Button onClick={() => { setEmailTo(contactEmail); setShowEmailDialog(true); }} data-testid="email-to-merchant-btn">
              <Mail className="h-4 w-4 mr-2" /> Email to Merchant
            </Button>
          </div>
        </Card>

        {/* Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={(v) => { setShowEmailDialog(v); if (!v) setEmailPreview(null); }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Send Proposal Email</DialogTitle>
              <DialogDescription>Send an industry-specific email with the proposal link to the merchant.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Recipient Email *</label>
                  <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="merchant@business.com" data-testid="email-to-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Recipient Name</label>
                  <Input value={contactName} readOnly className="bg-muted" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Email Template</label>
                  <Select value={industry} onValueChange={(v) => { setIndustry(v); setEmailPreview(null); }}>
                    <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="salon_spa">Salon & Spa</SelectItem>
                      <SelectItem value="auto_repair">Auto Repair</SelectItem>
                      <SelectItem value="medical_dental">Medical / Dental</SelectItem>
                      <SelectItem value="ecommerce">E-Commerce</SelectItem>
                      <SelectItem value="specialty_high_risk">Specialty / High-Risk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Schedule Send (optional)</label>
                  <Input type="datetime-local" value={emailScheduleAt} onChange={e => setEmailScheduleAt(e.target.value)} data-testid="email-schedule-input" />
                </div>
              </div>

              {emailPreview && (
                <div className="border rounded-lg overflow-hidden" data-testid="email-preview-panel">
                  <div className="bg-muted px-4 py-2 border-b">
                    <p className="text-sm font-semibold">{emailPreview.subject}</p>
                    <p className="text-xs text-muted-foreground">{emailPreview.template_name}</p>
                  </div>
                  <iframe
                    srcDoc={emailPreview.html_body}
                    title="Email Preview"
                    className="w-full h-56 border-0"
                    sandbox=""
                  />
                </div>
              )}

              <div className="flex justify-between items-center pt-2">
                <Button variant="ghost" size="sm" onClick={handlePreviewEmail} data-testid="preview-email-btn">
                  <Eye className="h-4 w-4 mr-1" /> Preview Email
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
                  <Button onClick={handleSendEmail} disabled={emailSending || !emailTo} data-testid="send-email-btn">
                    {emailSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    {emailScheduleAt ? 'Schedule' : 'Send Now'}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <div className="flex justify-center">
          <Button variant="ghost" onClick={() => { setStep('input'); setAnalysis(null); setStatementId(null); setPricingResult(null); setProposal(null); setEmailPreview(null); }}>
            Start New Analysis
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

// ── Statements List ──────────────────────────────────────────

function StatementsTab({ statements, loading }) {
  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  if (statements.length === 0) {
    return (
      <Card className="p-12 text-center">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold mb-2">No statements uploaded</h3>
        <p className="text-sm text-muted-foreground">Upload a merchant processing statement to get started.</p>
      </Card>
    );
  }

  const pricingLabels = {
    interchange_plus: 'Interchange+',
    flat_rate: 'Flat Rate',
    err: 'ERR',
    dual_pricing: 'Dual Pricing',
    tiered_3: '3-Tier',
    tiered_2: '2-Tier',
    tiered_4: '4-Tier',
  };

  const statusVariant = (s) => {
    if (s === 'proposal_ready') return 'default';
    if (s === 'analyzed') return 'default';
    if (s === 'analysis_error') return 'destructive';
    return 'secondary';
  };

  return (
    <div className="space-y-2">
      {statements.map(stmt => (
        <Card key={stmt.statement_id} className="p-4" data-testid={`statement-${stmt.statement_id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary/60" />
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{stmt.business_name || stmt.filename}</p>
                  <Badge variant={statusVariant(stmt.status)} data-testid={`stmt-status-${stmt.statement_id}`}>
                    {stmt.status === 'proposal_ready' ? 'Proposal Ready' : stmt.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  {stmt.analysis?.pricing_program && stmt.analysis.pricing_program !== 'unknown' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-medium" data-testid={`stmt-pricing-${stmt.statement_id}`}>
                      <BarChart3 className="w-2.5 h-2.5" />
                      {pricingLabels[stmt.analysis.pricing_program] || stmt.analysis.pricing_program}
                    </span>
                  )}
                  {stmt.analysis?.processor_name && <span>Processor: {stmt.analysis.processor_name}</span>}
                  {stmt.analysis?.total_volume && <span>Vol: ${Number(stmt.analysis.total_volume).toLocaleString()}</span>}
                  {stmt.analysis?.effective_rate && <span>Rate: {(stmt.analysis.effective_rate * 100).toFixed(2)}%</span>}
                  {stmt.uploaded_via === 'quick_send' && <span className="text-blue-500">via Quick-Send</span>}
                  <span>{new Date(stmt.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            {stmt.has_file && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost" size="sm"
                  data-testid={`stmt-view-${stmt.statement_id}`}
                  onClick={() => openStatementFile(stmt.statement_id, 'view')}
                >
                  <Eye className="h-3.5 w-3.5 mr-1" /> View
                </Button>
                <Button
                  variant="ghost" size="sm"
                  data-testid={`stmt-download-${stmt.statement_id}`}
                  onClick={() => openStatementFile(stmt.statement_id, 'download')}
                >
                  <Download className="h-3.5 w-3.5 mr-1" /> Download
                </Button>
              </div>
            )}
          </div>
          <StatementMerchant stmt={stmt} />
        </Card>
      ))}
    </div>
  );
}

function StatementMerchant({ stmt }) {
  const [linked, setLinked] = useState(Boolean(stmt.business_id));
  return (
    <>
    {stmt.analysis?.total_volume > 0 && (
      <details className="mt-3" open={stmt.status === 'analyzed'} data-testid={`stmt-proposal-${stmt.statement_id}`}>
        <summary className="cursor-pointer text-xs font-medium text-primary">Proposal · ready to send</summary>
        <div className="mt-2"><ProposalBuilder statementId={stmt.statement_id} /></div>
      </details>
    )}
    <details className="mt-3" data-testid={`stmt-merchant-${stmt.statement_id}`}>
      <summary className="cursor-pointer text-xs text-muted-foreground">Merchant {linked ? '· linked' : '· not linked'}</summary>
      <div className="mt-2"><MerchantContextPanel businessId={stmt.business_id} recordType="statement" recordId={stmt.statement_id} onChange={(id) => setLinked(Boolean(id))} /></div>
    </details>
    </>
  );
}

// ── Proposals List ───────────────────────────────────────────

function ProposalsTab({ proposals, loading, onRefresh }) {
  const [openContext, setOpenContext] = useState(null);
  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  if (proposals.length === 0) {
    return (
      <Card className="p-12 text-center">
        <FileCheck className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold mb-2">No proposals yet</h3>
        <p className="text-sm text-muted-foreground">Generate your first proposal from the Analyze tab.</p>
      </Card>
    );
  }

  const statusConfig = {
    draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
    sent: { color: 'bg-blue-100 text-blue-700', label: 'Sent' },
    viewed: { color: 'bg-amber-100 text-amber-700', label: 'Viewed' },
    accepted: { color: 'bg-green-100 text-green-700', label: 'Accepted' },
  };

  return (
    <div className="space-y-2">
      {proposals.map(p => {
        const sc = statusConfig[p.status] || statusConfig.draft;
        const url = `${API_BASE}/proposals/view/${p.proposal_token}`;
        return (
          <Card key={p.proposal_id} className="p-4" data-testid={`proposal-${p.proposal_id}`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm">{p.business_name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded ${sc.color}`}>{sc.label}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                  <span>Vol: ${Number(p.monthly_volume).toLocaleString()}/mo</span>
                  <span>Saves: ${Number(p.pricing_data?.merchant_savings || 0).toLocaleString()}/mo</span>
                  <span>{p.pricing_data?.name}</span>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => window.open(url, '_blank')}>
                  <Eye className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(url)}>
                  <Copy className="h-4 w-4" />
                </Button>
                {p.status === 'draft' && (
                  <Button variant="ghost" size="sm" onClick={async () => {
                    await api.request(`/proposals/${p.proposal_id}/send`, { method: 'PUT' });
                    onRefresh();
                  }}>
                    <Send className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpenContext(openContext === p.proposal_id ? null : p.proposal_id)}
                  title="Merchant context"
                  aria-label="Show canonical merchant context"
                  data-testid={`proposal-context-toggle-${p.proposal_id}`}
                >
                  <Building2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {openContext === p.proposal_id && (
              <div className="mt-4">
                {/* Same canonical component/API as Leads, Opportunities and Applications */}
                <MerchantContextPanel businessId={p.business_id} recordType="proposal" recordId={p.proposal_id} />
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
